import { useMutation } from '@tanstack/vue-query'
import type { CreateContractInput } from '~/resources/graphql/graphql'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useShowAlert } from '~/composables/use-show-alert'
import { useSetCartData } from '~/composables/use-cart'
import { useSelectedLanguage } from '~/composables/use-selected-language'

export function usePassCreateContractMutation() {
    const query = useRpQuery()
    const selectedLanguage = useSelectedLanguage()
    const activeFacility = useActiveFacility()
    const showAlert = useShowAlert()
    const setCartData = useSetCartData()

    return useMutation({
        mutationFn: async (mutationData: CreateContractInput) => {
            const response = await query(
                graphql(/** @lang GraphQL */ `
                    mutation PassCreateContractMutation(
                        $input: CreateContractInput!
                        $facilityId: ID!
                        $language: Language!
                    ) {
                        createContract(contractInput: $input) {
                            __typename
                            ... on CreateContractResult {
                                recordId
                                record {
                                    __typename
                                    ... on PlanContract {
                                        ...PurchasePassContractFragment
                                    }
                                }
                                query {
                                    viewer {
                                        cart(facilityId: $facilityId) {
                                            ...ViewerCartFragment
                                        }
                                    }
                                }
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData,
                    facilityId: activeFacility.value.id,
                    language: selectedLanguage.value
                }
            )

            if (response.createContract.__typename === 'CreateContractResult') {
                // Update cart
                if (response.createContract.query.viewer !== null) {
                    setCartData(response.createContract.query.viewer.cart)
                }

                if (
                    response.createContract.record.__typename === 'PlanContract'
                ) {
                    return response.createContract.record
                } else {
                    throw new Error()
                }
            }

            if (response.createContract.fieldErrors.length > 0) {
                await showAlert(
                    'Heads Up!',
                    response.createContract.fieldErrors[0].message
                )
            }

            throw new Error()
        }
    })
}
